import { Component, Inject, ViewChild, DOCUMENT, OnInit } from '@angular/core';
import {
  OBTRailJourneyOption,
  OBTRailQuoteResult,
  RailEnterpriseSearchInterface,
  RailSearchCriteria,
  OBTRailFareTypes,
  CheapestRailPrices,
  CheapestRailClassPrices,
  FlightEnterpriseSearchInterface,
  RailFareTypes,
  OBTRailJourneyFare,
  RailDirection,
  LocationTypes,
  SingleOrReturn
} from '@sabstravtech/obtservices/base';
import {
  EnterpriseSearchService,
  ModalOpenerService,
  RailSearchJourneyType,
  ServiceType,
  UserService
} from '@sabstravtech/obtservices/angular';
import moment from 'moment';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

import { LightningModalTypes } from '../../../../core/classes/modal-types.enum';
import { SearchToggleComponent } from '../../shared/search-toggle/search-toggle.component';
import { ThemeService } from '../../../../core/services/theme.service';
import { BaseResultsComponent } from '../../../../core/components/base_components/base-results.component';
import { ResultAwareService } from '@sabstravtech/obtservices/angular';
export enum EarlierOrLater {
  EARLIER = 'earlier',
  LATER = 'later'
}

@Component({
  selector: 'app-rail',
  templateUrl: './rail.component.html',
  styleUrls: ['./rail.component.scss'],
  standalone: false
})
export class RailComponent extends BaseResultsComponent implements OnInit {
  searchParams: RailEnterpriseSearchInterface;
  RailJourneyType = RailSearchJourneyType;
  RailSearchCriteria = RailSearchCriteria;
  flightSearchParams: FlightEnterpriseSearchInterface;
  ServiceType: typeof ServiceType = ServiceType;
  EarlierOrLater = EarlierOrLater;
  displayLowCo2Button = false;

  expandedTickets: Record<OBTRailFareTypes, Record<string, boolean>> = {
    returnJourneyFares: {},
    openReturnJourneyFares: {},
    dualSingleJourneyFares: {},
    singleJourneyFares: {},
    splitFares: {}
  };

  @ViewChild('searchToggle') searchToggle: SearchToggleComponent;

  constructor(
    private modalService: ModalOpenerService,
    private searchService: EnterpriseSearchService,
    private router: Router,
    private resultAwareService: ResultAwareService,
    titleService: Title,
    themeService: ThemeService,
    translateService: TranslateService,
    userService: UserService,
    @Inject(DOCUMENT) private document: Document
  ) {
    super(titleService, translateService, themeService, searchService, userService, router);
    this.searchParams = this.searchService.searches[ServiceType.Rail];
    this.flightSearchParams = this.searchService.searches[ServiceType.Flight];
    this.makeFareNames = this.makeFareNames.bind(this);
  }

  ngOnInit(): void {
    this.setResultsTitle('Rail results');
    this.expandedTickets = {
      returnJourneyFares: {},
      openReturnJourneyFares: {},
      dualSingleJourneyFares: {},
      singleJourneyFares: {},
      splitFares: {}
    };
    // Capture rail results for chatbot when they arrive
    let bookingWindowModalShown = false;

    this.subscribe(this.searchParams.isLoading, (isLoading: boolean) => {
      if (isLoading) {
        bookingWindowModalShown = false;
      }
    });

    this.subscribe(this.searchParams.results, (results) => {
      if (results?.[0]?.outbound?.length) {
        this.resultAwareService.captureRailResults(results[0]);
      }

      const noResults =
        !results?.length ||
        results[0]?.outbound?.length === 0 ||
        (results[0]?.outbound?.length === 0 && results[0]?.return?.length === 0);

      if (
        noResults &&
        !bookingWindowModalShown &&
        this.searchParams.outBoundDateTime?.diff(moment(), 'weeks') >= 12
      ) {
        bookingWindowModalShown = true;
        this.modalService.open(LightningModalTypes.InfoModalComponent, null, {
          title: $localize`:@@bookingWindowTitle:Booking Window`,
          info: $localize`:@@bookingWindowMessage:Train tickets are generally available to book up to 12 weeks before travel, once the relevant Train Operating Company (TOC) has confirmed availability and released its timetable in the booking system. As each TOC releases schedules at different times, some journeys may not be available as far in advance, particularly where engineering works need to be confirmed. In addition, some Advance tickets may only be released a few weeks before travel and can be subject to limited availability. If your chosen journey is not yet available to book, please check back closer to your travel date as tickets are released on a rolling basis.`,
          buttonText: 'Ok'
        });
      }
    });

    if (this.searchParams.results.value[0]) {
      this.searchParams.results.value[0].outbound?.forEach((journey) => {
        if (
          this.searchParams.results.value[0].outbound[0].co2PerPassenger !== journey.co2PerPassenger
        ) {
          this.displayLowCo2Button = true;
        }
      });
      this.searchParams.results.value[0].return?.forEach((journey) => {
        if (
          this.searchParams.results.value[0].return[0].co2PerPassenger !== journey.co2PerPassenger
        ) {
          this.displayLowCo2Button = true;
        }
      });
    }
  }

  cheapestReturn(results: OBTRailQuoteResult): number[] {
    return [];
  }

  skipToResultContent(): boolean {
    return super.skipToResultContent('trains-link');
  }

  clearHighlights(): void {
    const elemsWithHighlight = document.getElementsByClassName('highlight');
    Array.from(elemsWithHighlight).forEach((elemWithHighlight) => {
      elemWithHighlight.classList.remove('highlight');
    });
  }

  getCheapestDualOutbound(cheapest: {
    outboundCheapest: CheapestRailPrices;
    inboundCheapest: CheapestRailPrices;
    cheapestClassTicket: CheapestRailClassPrices;
    cheapestClassTicketReturn: CheapestRailClassPrices;
    cheapestClassTicketDual: CheapestRailClassPrices;
  }): number {
    const outbounds: number[] = [
      cheapest.outboundCheapest.dualSingleJourneyFares.price,
      cheapest.cheapestClassTicketDual.standardClassFare.price
    ].filter((item) => item > 0);
    return Math.min(...outbounds);
  }

  getCheapestDualInbound(cheapest: {
    outboundCheapest: CheapestRailPrices;
    inboundCheapest: CheapestRailPrices;
    cheapestClassTicket: CheapestRailClassPrices;
    cheapestClassTicketReturn: CheapestRailClassPrices;
    cheapestClassTicketDual: CheapestRailClassPrices;
  }): number {
    const inbounds = [
      cheapest.inboundCheapest.dualSingleJourneyFares.price,
      cheapest.cheapestClassTicketDual.inboundStandardClassFare.price
    ].filter((item) => item > 0);
    return Math.min(...inbounds);
  }

  highlightCheapestTickets(cheapestClasses: string[]): void {
    this.clearHighlights();
    if (cheapestClasses.length) {
      let scrolled = false;
      cheapestClasses.forEach((cheapestClass, index) => {
        const elemsWithCheapestClass = this.document.getElementsByClassName(cheapestClass);
        Array.from(elemsWithCheapestClass).forEach((elemWithCheapestClass) => {
          elemWithCheapestClass.classList.add('highlight');
          if (index === cheapestClasses.length - 1 && this.document.body.clientWidth > 1450) {
            elemWithCheapestClass.scrollIntoView({ block: 'center' });
            scrolled = true;
          }
        });
        if (!scrolled && index === cheapestClasses.length - 1) {
          // Ensures we scroll to the highlighted elements if it's missed during the last loop
          const elemsWithHighlight = this.document.getElementsByClassName('highlight');
          if (elemsWithHighlight.length && this.document.body.clientWidth > 1450) {
            elemsWithHighlight[elemsWithHighlight.length - 1].scrollIntoView({
              block: 'center'
            });
          }
        }
      });
      if (this.document.body.clientWidth < 1450) {
        const elemensWithHighlight = this.document.getElementsByClassName('highlight');
        if (elemensWithHighlight.length) {
          elemensWithHighlight[0].scrollIntoView({
            behavior: 'auto',
            block: 'center',
            inline: 'center'
          });
        }
      }
    }
  }
  resolveTabbingHighlight(): void {
    setTimeout(function () {
      document.body.classList.add('user-is-tabbing');
    }, 300);
  }

  reSearch(): void {
    localStorage.setItem('prevURL', window.location.href); // prevent going back to wellbeing which will no longer have correct results
    this.performReSearch(ServiceType.Rail);
  }

  getNextResultsPage(
    datetime: string,
    timeCriteria: RailSearchCriteria,
    firstJourney: OBTRailJourneyOption,
    lastJourney: OBTRailJourneyOption,
    type: EarlierOrLater,
    inbound?: boolean
  ): void {
    const fares = Object.keys(firstJourney.returnJourneyFares).length
      ? firstJourney.returnJourneyFares
      : Object.keys(firstJourney.singleJourneyFares).length
        ? firstJourney.singleJourneyFares
        : Object.keys(firstJourney.dualSingleJourneyFares).length
          ? firstJourney.dualSingleJourneyFares
          : Object.keys(firstJourney.openReturnJourneyFares).length
            ? firstJourney.openReturnJourneyFares
            : Object.keys(firstJourney.splitFares).length
              ? firstJourney.splitFares
              : null;

    const fareValues = Object.values(fares);

    fareValues.forEach((fareValue: any[]) => {
      fareValue.forEach((fare) => {
        if (fare && !this.searchParams.transactionIdentifier) {
          this.searchParams.transactionIdentifier = fare.identifiers.transactionIdentifier;
        }
      });
    });

    /**
     * * The below code is not an idea solution to define the best time window to move earlier
     * * Even we add or subtract 10/15 more minutes but the providers may still return the journey which was in the previous result.
     */
    if (inbound) {
      this.searchParams.returnCriteria = timeCriteria;
      if (type === EarlierOrLater.EARLIER) {
        // Get the time diff by comparing the depature time between the first journey and the last journey in the current result
        const earliestJourney = firstJourney.departDateTime;
        const latestJourney = lastJourney.departDateTime;
        const diff =
          earliestJourney !== latestJourney
            ? Math.max(moment(latestJourney).diff(moment(earliestJourney), 'minutes'), 45)
            : 90;
        // If only one journey is in the current result, default to going back 90 minutes.
        // Otherwise, use the minimum value of 45 minutes or the time between the first and last journey.
        this.searchParams.inBoundDateTime = moment(datetime)
          .subtract(diff, 'minutes')
          .subtract(15, 'minutes'); // Subtract 15 more minutes to get rid the earliest journey in the previous result
      } else {
        // Add 10 more minutes to get rid the latest journey in the previous result
        this.searchParams.inBoundDateTime = moment(datetime).add(10, 'minutes');
      }
    } else {
      this.searchParams.outboundCriteria = timeCriteria;
      if (type === EarlierOrLater.EARLIER) {
        // Get the time diff by comparing the depature time between the first journey and the last journey in the current result
        const earliestJourney = firstJourney.departDateTime;
        const latestJourney = lastJourney.departDateTime;
        const diff =
          earliestJourney !== latestJourney
            ? Math.max(moment(latestJourney).diff(moment(earliestJourney), 'minutes'), 45)
            : 90;
        // If only one journey is in the current result, default to going back 90 minutes.
        // Otherwise, use the minimum value of 45 minutes or the time between the first and last journey.
        this.searchParams.outBoundDateTime = moment(datetime)
          .subtract(diff, 'minutes')
          .subtract(15, 'minutes'); // Subtract 15 more minutes to get rid the earliest journey in the previous result
      } else {
        // Add 10 more minutes to get rid the latest journey in the previous result
        this.searchParams.outBoundDateTime = moment(datetime).add(10, 'minutes');
      }
    }

    if (this.searchParams.isValid.value) {
      this.searchService.search_objects[ServiceType.Rail].chosen = true;
      this.searchService.determineHighestSearchPriority();
      this.searchService.startSearches();
    } else {
      this.modalService.open(
        LightningModalTypes.ModalErrorComponent,
        { centered: true },
        { error: 'Search params appear to be invalid' }
      );
    }
  }

  getDifferentDayPage(type: EarlierOrLater, inbound?: boolean): void {
    if (inbound) {
      if (type === EarlierOrLater.EARLIER) {
        this.searchParams.inBoundDateTime = this.searchParams.inBoundDateTime.subtract(1, 'day');
      } else {
        this.searchParams.inBoundDateTime = this.searchParams.inBoundDateTime.add(1, 'day');
      }
    } else {
      if (type === EarlierOrLater.EARLIER) {
        this.searchParams.outBoundDateTime = this.searchParams.outBoundDateTime.subtract(1, 'day');
      } else {
        this.searchParams.outBoundDateTime = this.searchParams.outBoundDateTime.add(1, 'day');
      }
    }

    if (this.searchParams.isValid.value) {
      this.searchService.search_objects[ServiceType.Rail].chosen = true;
      this.searchService.determineHighestSearchPriority();
      this.searchService.startSearches();
    } else {
      this.modalService.open(
        LightningModalTypes.ModalErrorComponent,
        { centered: true },
        { error: 'Search params appear to be invalid' }
      );
    }
  }

  makeFareNames(journeys: OBTRailJourneyOption[]): Record<OBTRailFareTypes, string[]> {
    return [
      'returnJourneyFares',
      'openReturnJourneyFares',
      'dualSingleJourneyFares',
      'singleJourneyFares',
      'splitFares'
    ].reduce(
      (
        accum: Record<OBTRailFareTypes, string[]>,
        next: OBTRailFareTypes
      ): Record<OBTRailFareTypes, string[]> => {
        accum[next] = journeys?.length > 0 ? Object.keys(journeys[0][next]) : [];
        return accum;
      },
      {
        returnJourneyFares: [],
        openReturnJourneyFares: [],
        dualSingleJourneyFares: [],
        singleJourneyFares: [],
        splitFares: []
      }
    );
  }

  switchToFlight() {
    this.searchService.search_objects[ServiceType.Rail].chosen = false;
    this.router.navigate([this.searchService.search_objects[ServiceType.Flight].resultUrl]);
  }

  getFinalDisplayedResultIndex(resultBlock: OBTRailJourneyOption[]): number {
    if (window.screen.width <= 600) {
      // If mobile we only display 3 columns
      // If there are less than 3 results, get the last index, if not get the 3rd
      return resultBlock.length < 3 ? resultBlock.length - 1 : 2;
    } else {
      // Otherwise we display all
      return resultBlock.length - 1;
    }
  }

  switchToHotel(): void {
    // Set train station for hotel search
    if (this.searchParams.travellingTo) {
      this.searchService.searches[ServiceType.Hotel].trainsStation = this.searchParams.travellingTo;
    }

    // Check if it's a combo search
    if (
      !this.searchService.searches[ServiceType.Rail].isRailHotelComboSearch &&
      this.searchService.search_objects[ServiceType.Flight].chosen
    ) {
      this.searchService.search_objects[ServiceType.Flight].chosen = false;
    }

    // Navigate to hotel search with rail destination
    this.navigateToHotelSearch(
      ServiceType.Rail,
      {
        checkin: this.searchParams.outBoundDateTime,
        checkout: this.searchParams.inBoundDateTime
      },
      this.searchParams.travellingTo
        ? {
            ...this.searchParams.travellingTo,
            type: LocationTypes.TrainStation
          }
        : undefined
    );
  }

  earlier(
    isDateDifferent: boolean,
    data,
    journeyType: 'outbound' | 'return',
    useFinalResultIndex = false
  ): void {
    const journeyData = data.results[0][journeyType];
    const originalDateTime =
      journeyType === 'outbound'
        ? data.originalUserSearch.outBoundDateTime
        : data.originalUserSearch.inBoundDateTime;
    const datetime = isDateDifferent ? originalDateTime : journeyData[0].departDateTime;

    console.log(datetime);

    this.getNextResultsPage(
      datetime,
      RailSearchCriteria.Depart,
      journeyData[0],
      journeyData[this.getFinalDisplayedResultIndex(journeyData)],
      EarlierOrLater.EARLIER,
      useFinalResultIndex
    );
  }

  openCheapestFareFinder(): void {
    this.modalService
      .open(
        LightningModalTypes.CheapestFareFinderModalComponent,
        { centered: true, size: 'xl', windowClass: 'cff-modal' },
        {
          outboundJourneys: this.searchParams.results.value[0].outbound,
          inboundJourneys: this.searchParams.results.value[0].return,
          sourceId: this.searchParams.results.value[0].sourceId
        }
      )
      .then((result) => {
        if (result?.length) {
          this.searchParams.selectTicket(
            RailDirection.Outbound,
            result[0].journey,
            result[0].fare,
            null,
            this.getTicketType(result[0].journey, result[0].fare)
          );
          this.searchParams.selectTicket(
            RailDirection.Inbound,
            result[1].journey,
            result[1].fare,
            null,
            this.getTicketType(result[1].journey, result[1].fare)
          );
        }
      });
  }

  getTicketType(journey: OBTRailJourneyOption, railFare: OBTRailJourneyFare) {
    let ticketType;
    Object.keys(journey.dualSingleJourneyFares).forEach((key) => {
      const arrayOfTickets = journey.dualSingleJourneyFares[key];
      arrayOfTickets.forEach((ticket) => {
        if (ticket?.fareHash === railFare.fareHash) {
          ticketType = RailFareTypes.dualSingleJourneyFares;
        }
      });
    });
    Object.keys(journey.openReturnJourneyFares).forEach((key) => {
      const arrayOfTickets = journey.openReturnJourneyFares[key];
      arrayOfTickets.forEach((ticket) => {
        if (ticket?.fareHash === railFare.fareHash) {
          ticketType = RailFareTypes.openReturnJourneyFares;
        }
      });
    });
    Object.keys(journey.returnJourneyFares).forEach((key) => {
      const arrayOfTickets = journey.returnJourneyFares[key];
      arrayOfTickets.forEach((ticket) => {
        if (ticket?.fareHash === railFare.fareHash) {
          ticketType = RailFareTypes.returnJourneyFares;
        }
      });
    });
    Object.keys(journey.singleJourneyFares).forEach((key) => {
      const arrayOfTickets = journey.singleJourneyFares[key];
      arrayOfTickets.forEach((ticket) => {
        if (ticket?.fareHash === railFare.fareHash) {
          ticketType = RailFareTypes.singleJourneyFares;
        }
      });
    });
    Object.keys(journey.splitFares).forEach((key) => {
      const arrayOfTickets = journey.splitFares[key];
      arrayOfTickets.forEach((ticket) => {
        if (ticket?.fareHash === railFare.fareHash) {
          ticketType = RailFareTypes.splitFares;
        }
      });
    });
    return ticketType;
  }

  openReturn(): void {
    this.searchParams.chosenSearchType = RailSearchJourneyType.ReturnJourney;
    this.searchToggle.toggleState = true;
    this.document.getElementById('rail_research_container').scrollIntoView();
    this.searchParams.cheapest.value;
    setTimeout(() => {
      // Wait for research container to open
      this.document.getElementById('rail-toggle-return').getElementsByTagName('section')[0].focus();
    }, 0);
  }

  getCheapestCo2Ticket(data: any) {
    const isReturn =
      data.originalUserSearch.chosenSearchType === this.RailJourneyType.ReturnJourney ||
      data.originalUserSearch.chosenSearchType === this.RailJourneyType.OpenReturnJourney;
    if (isReturn) {
      let cheapestTickets: {
        totalPrice: number;
        out: OBTRailJourneyFare;
        in: OBTRailJourneyFare;
      };
      const dualPrice =
        data.cheapest.cheapestTicketLowCo2Outbound.dualSingleJourneyFares.price +
        data.cheapest.cheapestTicketLowCo2Inbound.dualSingleJourneyFares.price;
      const returnPrice = data.cheapest.cheapestTicketLowCo2Outbound.returnJourneyFares.price;
      const openReturnPrice =
        data.cheapest.cheapestTicketLowCo2Outbound.openReturnJourneyFares.price;

      cheapestTickets = {
        totalPrice: dualPrice,
        out: data.cheapest.cheapestTicketLowCo2Outbound.dualSingleJourneyFares.fare,
        in: data.cheapest.cheapestTicketLowCo2Inbound.dualSingleJourneyFares.fare
      };

      if (
        returnPrice > 0 &&
        (returnPrice < cheapestTickets.totalPrice || cheapestTickets.totalPrice === 0)
      ) {
        cheapestTickets = {
          totalPrice: returnPrice,
          out: data.cheapest.cheapestTicketLowCo2Outbound.returnJourneyFares.fare,
          in: data.cheapest.cheapestTicketLowCo2Inbound.returnJourneyFares.fare
        };
      }

      if (
        openReturnPrice > 0 &&
        (openReturnPrice < cheapestTickets.totalPrice || cheapestTickets.totalPrice === 0)
      ) {
        cheapestTickets = {
          totalPrice: openReturnPrice,
          out: data.cheapest.cheapestTicketLowCo2Outbound.openReturnJourneyFares.fare,
          in: data.cheapest.cheapestTicketLowCo2Inbound.openReturnJourneyFares.fare
        };
      }
      return cheapestTickets;
    } else {
      const { standardClassFare, firstClassFare } = data.cheapest.cheapestClassTicketLowCo2;
      const cheapestFare =
        standardClassFare?.price > 0 && firstClassFare?.price > 0
          ? standardClassFare.price < firstClassFare.price
            ? standardClassFare
            : firstClassFare
          : standardClassFare?.price > 0
            ? standardClassFare
            : firstClassFare?.price > 0
              ? firstClassFare
              : standardClassFare;

      const cheapestTickets = {
        totalPrice: cheapestFare.price,
        out: cheapestFare.fare
      };

      return cheapestTickets;
    }
  }

  highlightCO2Tickets(cheapestTicket: {
    totalPrice: number;
    out: OBTRailJourneyFare;
    in?: OBTRailJourneyFare;
  }): void {
    this.clearHighlights();
    if (cheapestTicket) {
      let scrolled = false;
      const lowCo2Rows = this.document.getElementsByClassName('low_co2_row');
      Array.from(lowCo2Rows).forEach((row, index) => {
        const outboundElemsWithCheapestClass = row.getElementsByClassName(
          `outbound-${cheapestTicket.out.class}-${cheapestTicket.out.price}`
        );
        if (
          this.searchParams.originalUserSearch.chosenSearchType ===
          this.RailJourneyType.ReturnJourney
        ) {
          const inboundElemsWithCheapestClass = row.getElementsByClassName(
            `inbound-${cheapestTicket.out.singleOrReturn === SingleOrReturn.Return ? cheapestTicket.out.class : cheapestTicket.in.class}-${cheapestTicket.in.price}`
          );
          Array.from(outboundElemsWithCheapestClass).forEach((elemWithCheapestClass) => {
            elemWithCheapestClass.classList.add('highlight');
            if (index === lowCo2Rows.length - 1 && this.document.body.clientWidth > 1450) {
              elemWithCheapestClass.scrollIntoView({ block: 'center' });
              scrolled = true;
            }
          });
          Array.from(inboundElemsWithCheapestClass).forEach((elemWithCheapestClass) => {
            elemWithCheapestClass.classList.add('highlight');
            if (index === lowCo2Rows.length - 1 && this.document.body.clientWidth > 1450) {
              elemWithCheapestClass.scrollIntoView({ block: 'center' });
              scrolled = true;
            }
          });
          if (!scrolled && index === lowCo2Rows.length - 1) {
            // Ensures we scroll to the highlighted elements if it's missed during the last loop
            const elemsWithHighlight = document.getElementsByClassName('highlight');
            if (elemsWithHighlight.length && this.document.body.clientWidth > 1450) {
              elemsWithHighlight[elemsWithHighlight.length - 1].scrollIntoView({
                block: 'center'
              });
            }
          }
        } else {
          Array.from(outboundElemsWithCheapestClass).forEach((elemWithCheapestClass) => {
            elemWithCheapestClass.classList.add('highlight');
            if (index === lowCo2Rows.length - 1 && this.document.body.clientWidth > 1450) {
              elemWithCheapestClass.scrollIntoView({ block: 'center' });
              scrolled = true;
            }
          });
          if (
            !scrolled &&
            index === lowCo2Rows.length - 1 &&
            this.document.body.clientWidth > 1450
          ) {
            // Ensures we scroll to the highlighted elements if it's missed during the last loop
            const elemsWithHighlight = document.getElementsByClassName('highlight');
            if (elemsWithHighlight.length) {
              elemsWithHighlight[elemsWithHighlight.length - 1].scrollIntoView({
                block: 'center'
              });
            }
          }
        }
      });
      if (this.document.body.clientWidth < 1450) {
        const elemensWithHighlight = this.document.getElementsByClassName('highlight');
        if (elemensWithHighlight.length) {
          elemensWithHighlight[0].scrollIntoView({
            behavior: 'auto',
            block: 'center',
            inline: 'center'
          });
        }
      }
    }
  }
}
